import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Popover, Select, Switch } from 'antd';
import { BASE_URL } from '../../conf/config';
import {
    distinct, effective, fmt2, groupDocuments, invoiceLine, isEmpty, issuesOf, pyFloat, shippingLine,
} from './documentModel';
import { explain, SOURCES } from './sources';
import './DocumentView.css';

// The order's identity: shown, changed with Edit, not here
const IDENTITY = new Set(['order_ID', 'position_ID', 'CPN', 'customer']);
const cls = (...names) => names.filter(Boolean).join(' ');
const width = (...texts) => Math.max(4, ...texts.map((t) => String(t ?? '').length)) + 1;
const chunks = (list, size) => {
    const out = [];
    for (let n = 0; n < list.length; n += size) out.push(list.slice(n, n + size));
    return out;
};
const capital = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The generate dialog shaped like the document: only what varies per document is shown (text that is
 * fixed in the template is left out), order fields are editable where they are printed, and values
 * taken from elsewhere can be traced to their source. Edits go straight into `orders` (the dialog's
 * state); Generate hands them to the usual submit.
 */
export default function DocumentView({
    docType, spec, button, positionsPerFile, orders, setOrders,
    fieldMapping, fieldOptions, fetchWithAuth, onGenerate, onCancel,
}) {
    const label = useCallback((f) => fieldMapping[f] || f, [fieldMapping]);
    const [context, setContext] = useState({});
    const [contextError, setContextError] = useState('');
    const [docIndex, setDocIndex] = useState(0);
    const [showSources, setShowSources] = useState(true);
    const [otherFields, setOtherFields] = useState(spec.other);

    const docs = useMemo(() => groupDocuments(orders, spec.group_by, button.combine), [orders, spec.group_by, button.combine]);
    const current = Math.max(0, Math.min(docIndex, docs.length - 1));
    const doc = docs[current] || { key: '', indices: [] };
    const last = doc.indices.length ? doc.indices[doc.indices.length - 1] : 0;
    const perFile = positionsPerFile || 4;

    // values from the customer, article and contact profile come from the backend, for the orders as they are now
    const payload = JSON.stringify(orders);
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchWithAuth(`${BASE_URL}/api/document-context/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: docType, data: orders }),
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.orders) { setContext(data.orders); setContextError(''); }
                    else setContextError(String(data.errors || data.detail || 'could not load customer and article data'));
                })
                .catch((e) => setContextError(e.message));
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload, docType]);

    const ctxOf = (i) => context[orders[i]?.id] || {};
    const fillOf = (i, field) => ctxOf(i).fills?.[field];
    const setField = (indices, field, value) =>
        setOrders((prev) => prev.map((o, i) => (indices.includes(i) ? { ...o, [field]: value } : o)));

    const issues = useMemo(() => docs.map((d) => issuesOf(d, orders, spec, context, label)), [docs, orders, spec, context, label]);
    const errors = issues.flatMap((list, di) => list.filter((x) => x.level === 'error').map((x) => ({ ...x, di })));
    const warnings = issues.flatMap((list, di) => list.filter((x) => x.level === 'warning').map((x) => ({ ...x, di })));

    // flip with the arrow keys, unless typing
    useEffect(() => {
        const onKey = (e) => {
            if (e.target.closest && e.target.closest('input, textarea, select, .ant-select')) return;
            if (e.key === 'ArrowLeft') setDocIndex((i) => Math.max(0, Math.min(i, docs.length - 1) - 1));
            if (e.key === 'ArrowRight') setDocIndex((i) => Math.min(docs.length - 1, i + 1));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [docs.length]);

    // ---- building blocks ----

    const positionsWith = (field, value) =>
        doc.indices.filter((i) => String(orders[i][field] ?? '') === value).map((i) => `Pos ${orders[i].position_ID}`).join(', ');

    // several positions of one document disagree on a value the document prints once
    const conflict = (field, values) => {
        const identity = IDENTITY.has(field);
        return (
            <Popover
                trigger="click"
                title={`${values.length} different values for ${label(field)}`}
                content={(
                    <div className="dv-pop">
                        <div>The {spec.label} prints one: the last position's (<b>{String(orders[last][field] ?? '') || 'empty'}</b>).</div>
                        {identity
                            ? <div>{label(field)} is the order's own number, so it isn't changed here.</div>
                            : values.map((v) => (
                                <Button key={v} block size="small" className="dv-opt" onClick={() => setField(doc.indices, field, v)}>
                                    <span>{v || '(empty)'}</span><small>{positionsWith(field, v)}</small>
                                </Button>
                            ))}
                    </div>
                )}
            >
                <span className="dv-conflict" tabIndex={0} role="button">{values.length} values{identity ? '' : ' · pick one'}</span>
            </Popover>
        );
    };

    const input = (value, fill, { indices, field, size, wide, strong }) => {
        const required = spec.required.includes(field);
        const missing = required && isEmpty(value) && isEmpty(fill);
        const applies = indices.length > 1 ? ` · applies to all ${indices.length} positions` : '';
        return (
            <input
                className={cls('f', required && 'req', missing && 'missing', wide && 'wide', strong && 'strong')}
                value={value}
                placeholder={fill ?? ''}
                size={size || width(value, fill)}
                aria-label={label(field)}
                title={`${label(field)}${required ? ' · required' : ''}${applies}${fill && isEmpty(value) ? ` · left empty: "${fill}"` : ''}`}
                onChange={(e) => setField(indices, field, e.target.value)}
            />
        );
    };

    // header field: printed once, stored on every position of the document
    const h = (field, opts = {}) => {
        const values = distinct(orders, doc.indices, field);
        if (values.length > 1) return conflict(field, values);
        if (IDENTITY.has(field)) return <span title={`${label(field)} (change it with Edit)`}>{values[0]}</span>;
        return input(values[0], fillOf(last, field), { ...opts, indices: doc.indices, field });
    };

    // line field: one per position
    const l = (i, field, opts = {}) => {
        const value = orders[i][field] ?? '';
        if (IDENTITY.has(field)) return <span className={cls(opts.strong && 'strong')} title={`${label(field)} (change it with Edit)`}>{value}</span>;
        return input(value, fillOf(i, field), { ...opts, indices: [i], field });
    };

    // value from elsewhere: shown, not edited; a click always says where it comes from and where to change it
    const src = (kind, key, text, i = last) => {
        const shown = String(text ?? '').trim() || '—';
        const x = explain(key, { ctx: ctxOf(i), order: orders[i], docType, template: spec.template });
        return (
            <Popover trigger="click" title={<span className={`dv-k-${x.kind}`}>{SOURCES[x.kind].name}</span>}
                content={<div className="dv-pop"><b>{x.title}</b><div>{x.body}</div>{x.note && <div className="muted">{x.note}</div>}</div>}>
                <span className={`src src-${kind}`} tabIndex={0} role="button">{shown}</span>
            </Popover>
        );
    };

    // required fields are marked wherever their label is
    const star = (name) => spec.required.includes(name) && <span className="req-star" title="Required">*</span>;

    // a labelled field in the details row
    const field = (caption, content, name) => <label className="dv-field"><span>{caption}{name && star(name)}</span>{content}</label>;

    const fileHead = (n, files, colSpan) => files > 1 && (
        <tr className="dv-file"><td colSpan={colSpan}>File {n + 1} of {files} · own PDF with the same number and its own totals</td></tr>
    );

    // ---- invoice ----

    const invoiceBody = () => {
        const lc = ctxOf(last);
        const c = lc.customer;
        const cur = c?.currency ?? '';
        const files = chunks(doc.indices, perFile);
        return (
            <>
                <div className="dv-top">
                    <div className="dv-addr">{(c?.billing ?? ['…']).map((line, k) => <div key={k}>{src('customer', 'billing', line)}</div>)}</div>
                    <dl className="dv-info">
                        <dt>Invoice no.</dt><dd>{h('invoice_ID', { strong: true })}</dd>
                        <dt>Invoice date</dt><dd>{h('invoice_date')}</dd>
                        <dt>Reference</dt><dd>{h('order_ID')}</dd>
                        <dt>Delivery date</dt><dd>{h('shipping_date_real')}</dd>
                        <dt>Customer no.</dt><dd>{src('customer', 'customer_ID', c?.customer_ID)}</dd>
                        <dt>VAT-ID</dt><dd>{src('customer', 'vat_ID', c?.vat_ID)}</dd>
                        <dt>Contact</dt><dd>{src('profile', 'contact_person', lc.contact?.person)}</dd>
                    </dl>
                </div>
                <div className="dv-fields">
                    {field('Tracking no.', h('tracking_ID'), 'tracking_ID')}
                    {field('Packing list no.', h('shipping_ID'), 'shipping_ID')}
                    {field('Incoterm', <span className="dv-pair">{h('incoterm', { size: 5 })}{h('fob_point')}</span>, 'incoterm')}
                    {field('HS code', h('HS'), 'HS')}
                    {field('Note', h('note', { size: 36 }), 'note')}
                </div>
                <table className="dv-table">
                    <thead><tr><th>Item</th><th>Article no.</th><th>Description</th><th>Quantity</th><th className="num">Unit price</th><th className="num">Total</th></tr></thead>
                    <tbody>
                        {files.map((part, n) => {
                            let net = 0; let tax = 0; let vatLabel = '';
                            const rows = part.map((i) => {
                                const o = orders[i];
                                const ctx = ctxOf(i);
                                const line = invoiceLine(o, ctx);
                                if (line) { net += line.total; tax += line.tax; vatLabel = line.vatLabel; }
                                return (
                                    <tr key={o.id}>
                                        <td>{l(i, 'position_ID')}</td>
                                        <td className="fill">{l(i, 'article_ID', { wide: true })}</td>
                                        <td><b>{o.CPN}</b> <span className="muted">{src('article', 'description', ctx.article?.description, i)}</span></td>
                                        <td className="nowrap">{l(i, 'quantity', { size: 8 })}{line && <div className="muted small">{src('calc', 'display_quant', line.displayQuant, i)}</div>}</td>
                                        <td className="num">{line ? src('article', 'unit_price', `${line.displayUnitPrice} ${cur}`, i) : '—'}</td>
                                        <td className="num">{line ? src('calc', 'line_total', `${fmt2(line.total)} ${cur}`, i) : '—'}</td>
                                    </tr>
                                );
                            });
                            return (
                                <React.Fragment key={n}>
                                    {fileHead(n, files.length, 6)}
                                    {rows}
                                    <tr className="sum"><td colSpan={5}>Total net</td><td className="num">{src('calc', 'sum_net', `${fmt2(net)} ${cur}`)}</td></tr>
                                    <tr className="sum light"><td colSpan={5}>VAT · {src('calc', 'vat', vatLabel)}</td><td className="num">{src('calc', 'vat', `${fmt2(tax)} ${cur}`)}</td></tr>
                                    <tr className="sum"><td colSpan={5}>Total gross</td><td className="num">{src('calc', 'gross', `${fmt2(net + tax)} ${cur}`)}</td></tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                <div className="dv-bottom">
                    {field('Payment due', h('invoice_due'))}
                    <div className="dv-meta">
                        <div>Contact email {src('profile', 'contact_email', lc.contact?.email)}</div>
                        <div>Bank {src('customer', 'bank', c?.bank)} · IBAN {src('customer', 'bank', c?.account)} · BIC {src('customer', 'bank', c?.bic)}</div>
                    </div>
                </div>
            </>
        );
    };

    // ---- shipping note ----

    const shippingBody = () => {
        const lc = ctxOf(last);
        const c = lc.customer;
        const orderIds = [...new Set(doc.indices.map((i) => orders[i].order_ID))].sort().join(', ');
        const files = chunks(doc.indices, perFile);
        const totals = files.map((part) => part.reduce((sum, i) => sum + shippingLine(orders[i], ctxOf(i)).totalWeight, 0));
        return (
            <>
                <div className="dv-top">
                    <div className="dv-addr">{(c?.shipping ?? ['…']).map((line, k) => <div key={k}>{src('customer', 'shipping', line)}</div>)}</div>
                    <dl className="dv-info">
                        <dt>Packing list no.{star('shipping_ID')}</dt><dd>{h('shipping_ID', { strong: true })}</dd>
                        <dt>PO/SA no.</dt><dd>{src('calc', 'order_ids', orderIds)}</dd>
                        <dt>Date</dt><dd>{h('shipping_date_real')}</dd>
                        <dt>Contact</dt><dd>{src('profile', 'contact_person', lc.contact?.person)}</dd>
                    </dl>
                </div>
                <div className="dv-fields">
                    {field('Tracking no.', h('tracking_ID'), 'tracking_ID')}
                    {field('HS code', h('HS'), 'HS')}
                    {field('Note', h('note', { size: 36 }), 'note')}
                </div>
                <table className="dv-table">
                    <thead><tr><th>#</th><th>Description</th><th>Manufacturer PN</th><th>Customer PN</th><th>Qty{star('quantity')}</th><th>Qty/CTN</th>
                        <th>CTN size</th><th>CTNs</th><th>kg/CTN</th><th>kg</th><th>Palettes</th></tr></thead>
                    <tbody>
                        {files.map((part, n) => (
                            <React.Fragment key={n}>
                                {fileHead(n, files.length, 11)}
                                {part.map((i, k) => {
                                    const o = orders[i];
                                    const ctx = ctxOf(i);
                                    const line = shippingLine(o, ctx);
                                    return (
                                        <tr key={o.id}>
                                            <td>{k + 1}</td>
                                            <td className="muted small">{src('article', 'short', ctx.article?.description_short, i)}</td>
                                            <td className="fill">{l(i, 'article_ID', { wide: true })}</td>
                                            <td>{l(i, 'CPN')}</td>
                                            <td>{l(i, 'quantity', { size: 7 })}</td>
                                            <td>{l(i, 'qty_per_ctn', { size: 5 })}</td>
                                            <td>{l(i, 'dimension_ctn', { size: 9 })}</td>
                                            <td>{l(i, 'ctn', { size: 4 })}</td>
                                            <td>{l(i, 'Gross_weight_ctn', { size: 5 })}</td>
                                            <td>{l(i, 'gross_weight', { size: 7 })}</td>
                                            <td className="nowrap">{l(i, 'palettes', { size: 3 })} × {l(i, 'weight_palette', { size: 4 })}
                                                {line.hasPalettes && <div className="muted small">{src('calc', 'palette_weight', `${o.palettes} pcs ${line.paletteWeight.toFixed(1)} kg`, i)}</div>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                <div className="dv-bottom">
                    <table className="dv-batch">
                        <thead><tr><th>Position</th><th>Batch qty.{star('batch_quantities')}</th><th>Batches{star('batches')}</th></tr></thead>
                        <tbody>
                            {doc.indices.map((i) => (
                                <tr key={orders[i].id}>
                                    <td className="muted">{orders[i].position_ID} · {orders[i].order_ID}</td>
                                    <td>{l(i, 'batch_quantities', { size: 12 })}</td>
                                    <td>{l(i, 'batches', { size: 18 })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="dv-meta">
                        {totals.map((t, n) => (
                            <div key={n}>Total weight{files.length > 1 ? ` (file ${n + 1})` : ''} {src('calc', 'total_weight', `${pyFloat(t)} kg`)}</div>
                        ))}
                        <div className="muted small">Several batches: separate with ; in both fields, same order.</div>
                    </div>
                </div>
            </>
        );
    };

    // ---- other order fields (the dropdown picker, vertical) ----

    const printed = new Set([...spec.header, ...spec.line, 'id', 'customer', 'order_ID', 'position_ID', 'CPN', 'inv_sig']);
    const otherOptions = fieldOptions
        .filter((o) => !printed.has(o.value))
        .map((o) => ({ value: o.value, label: `${label(o.value)}${spec.required.includes(o.value) ? ' *' : ''}` }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const addField = () => {
        const next = otherOptions.find((o) => !otherFields.includes(o.value));
        if (next) setOtherFields((prev) => [...prev, next.value]);
    };

    // ---- the dialog ----

    if (!orders.length) return <div className="docview">No orders selected.</div>;
    const n = docs.length;
    const positions = orders.length;
    const docName = doc.key === 'combined' ? 'one new number for all' : (doc.key || 'new number on generate');
    const fileCount = chunks(doc.indices, perFile).length;

    return (
        <div className="docview">
            <div className="dv-bar">
                <button type="button" className="flip" disabled={current === 0} onClick={() => setDocIndex(current - 1)} aria-label="Previous document">‹</button>
                <div className="dv-where">
                    <b>{capital(spec.label)} {current + 1} of {n}</b>
                    <span className="muted"> · {docName} · {doc.indices.length} position{doc.indices.length > 1 ? 's' : ''}{fileCount > 1 ? ` · ${fileCount} files` : ''}</span>
                    {n > 1 && (
                        <span className="dots">
                            {docs.map((d, di) => (
                                <button key={di} type="button" aria-label={`${spec.label} ${di + 1}`} onClick={() => setDocIndex(di)}
                                    className={cls('dot', di === current && 'on', issues[di].some((x) => x.level === 'error') && 'issue')} />
                            ))}
                        </span>
                    )}
                </div>
                <button type="button" className="flip" disabled={current === n - 1} onClick={() => setDocIndex(current + 1)} aria-label="Next document">›</button>
                <label className="dv-switch" data-help-id="doc-sources" title="Shade values by where they come from. Click any value to see where it comes from and where to change it.">
                    <Switch size="small" checked={showSources} onChange={setShowSources} /> Sources
                </label>
            </div>
            {contextError && <div className="dv-error">Customer and article data not loaded: {contextError}</div>}

            <article className={cls('dv-paper', showSources && 'sources')}>
                {docType === 'invoice' ? invoiceBody() : shippingBody()}
            </article>

            <section className="dv-extra">
                <div className="dv-extra-head">Other order fields</div>
                <div className="dv-extra-scroll">
                    <table>
                        <thead><tr><th />{doc.indices.map((i) => <th key={i}>Pos {orders[i].position_ID} · {orders[i].order_ID}</th>)}<th /></tr></thead>
                        <tbody>
                            {otherFields.map((f, k) => (
                                <tr key={`${k}-${f}`}>
                                    <td>
                                        <Select size="small" showSearch optionFilterProp="label" style={{ width: 190 }} value={f} options={otherOptions}
                                            onChange={(v) => setOtherFields((prev) => prev.map((x, j) => (j === k ? v : x)))} />
                                    </td>
                                    {doc.indices.map((i) => {
                                        const missing = spec.required.includes(f) && isEmpty(effective(orders[i], f, ctxOf(i).fills));
                                        return (
                                            <td key={i}>
                                                <input className={cls('f', spec.required.includes(f) && 'req', missing && 'missing')} value={orders[i][f] ?? ''} placeholder={fillOf(i, f) ?? ''} size={16}
                                                    aria-label={`${label(f)}, position ${orders[i].position_ID}`} onChange={(e) => setField([i], f, e.target.value)} />
                                            </td>
                                        );
                                    })}
                                    <td><button type="button" className="dv-x" aria-label="Remove this field" onClick={() => setOtherFields((prev) => prev.filter((_, j) => j !== k))}>×</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Button size="small" type="link" onClick={addField} style={{ paddingLeft: 0 }}>+ Add field</Button>
            </section>

            <footer className="dv-foot">
                <div className="dv-status">
                    {errors.length === 0
                        ? <span className="ok">Ready · {n} {n > 1 ? spec.plural : spec.label}, {positions} position{positions > 1 ? 's' : ''}</span>
                        : <span className="bad">{errors.length} thing{errors.length > 1 ? 's' : ''} to fix</span>}
                    {spec.required.length > 0 && <span className="muted"> · <span className="req-star">*</span> required</span>}
                    {(errors.length > 0 || warnings.length > 0) && (
                        <ul>
                            {[...errors, ...warnings].map((x, k) => (
                                <li key={k} className={x.level}>
                                    {n > 1 && <><button type="button" className="link" onClick={() => setDocIndex(x.di)}>{capital(spec.label)} {x.di + 1}</button>: </>}
                                    {x.text}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="dv-actions">
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button type="primary" data-help-id="generate-submit" disabled={errors.length > 0} onClick={() => onGenerate(orders)}>
                        Generate {n > 1 ? `${n} ${spec.plural}` : spec.label}
                    </Button>
                </div>
            </footer>
        </div>
    );
}
